import { Injectable } from '@angular/core';
import {
  catchError,
  concat,
  concatMap,
  filter,
  forkJoin,
  from,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  race,
  share,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timeout
} from 'rxjs';
import { BaseDomainModel } from '../../../models/base/base-domain-model';
import { Asset } from '../../../models/image/dto/asset';
import { BudsenseFile } from '../../../models/shared/budsense-file';
import { CopyAssetToTargetRequest } from '../../../models/image/requests/copy-asset-to-target-request';
import { GenerateUploadUrlRequest } from '../../../models/image/requests/generate-upload-url-request';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { SignedUploadUrl } from '../../../models/shared/signed-upload-url';
import { UploadFilePath } from '../../../models/enum/dto/upload-file.path';
import { exists } from '../../../functions/exists';
import { MediaUtils } from '../../../utils/media-utils';
import { StringUtils } from '../../../utils/string-utils';
import { UserDomainModel } from '../../user-domain-model';
import { MediaManagerStore } from '../store';
import {
  applyUploadedAssetToInMemoryState,
  normalizeCopiedAssetForMediaManager,
  normalizeFolderIdForAssetLibrary,
  normalizeUploadedAssetForTargetFolder,
  toBackendAssetLibraryParentFolderId
} from '../mutations';
import { buildMultiUploadValidationEvent, createInitialMultiUploadItems, createMultiUploadBatchId } from '../upload';
import { preflightGenerateUploadUrlValidation } from '../validation';
import { MediaManagerRemoteAssetSyncService } from '../sync/media-manager-remote-asset-sync.service';
import { AssetRepository } from '../../../repositories/asset/asset-repository';
import {
  BatchStartedEvent,
  MediaManagerMultiUploadBatchContext,
  MediaManagerMultiUploadBatchEvent,
  MediaManagerMultiUploadItemEvent,
  MediaManagerMultiUploadItemProcessingContext,
  MediaManagerMultiUploadItemState
} from 'src/app/models/media-manager/media-manager-multi-upload-state';
import { MediaManagerFileSystem } from 'src/app/models/media-manager/media-manager-file-system';

interface MediaManagerResolvedUploadAsset {
  asset: Asset;
  resolutionMessage: string | null;
}

/**
 * Orchestrates multi-file uploads end-to-end: validates each file locally, generates the signed S3 URL,
 * puts the file to S3, then reconciles the resulting asset (either via websocket events or a fallback
 * refetch) into the domain model's in-memory state. Also handles single-shot copies from an external
 * asset library into Media Manager.
 *
 * The reducer that turns the raw event stream into MediaManagerMultiUploadState is a pure module
 * (../upload/multi-upload-reducer). This service owns the imperative side: the batch launcher, per-item
 * lifecycle wiring, and the S3 PUT.
 *
 * Websocket message subscriptions live on the store (see assetUploadedMessages$,
 * assetTranscodeUpdateMessages$, duplicateAssetMessages$) so both this service and
 * MediaManagerRemoteAssetSyncService can share the same underlying stream.
 *
 * Bootstrapped eagerly by the logged-in scope so the multi-upload subscriptions
 * (setupMultiUploadLaunchSub + setupMultiUploadRefreshConductorSub) attach the moment the scope activates.
 */
@Injectable() // Provided by Logged In Scope
export class MediaManagerUploadService extends BaseDomainModel {
  constructor(
    private assetRepository: AssetRepository,
    private userDomainModel: UserDomainModel,
    private store: MediaManagerStore,
    private assetSync: MediaManagerRemoteAssetSyncService
  ) {
    super();
    this.turnOnMultiUploadStatePipeline();
    this.setupMultiUploadLaunchSub();
    this.setupMultiUploadRefreshConductorSub();
  }

  // Number of files pushed to S3 concurrently during a multi-file upload. Uploads run in parallel up to
  // this cap so a large batch finishes the S3 phase quickly, rather than PUTting one file at a time.
  private readonly maxConcurrentMultiUploads = 3;
  private readonly companyId = -1;

  /**
   * Start a fresh multi-file upload batch. Rejects if an in-flight batch has not yet completed. Emits when
   * the whole batch reaches the 'completed' status (all items finalized, one way or the other).
   */
  uploadMultipleAssets$(
    files: BudsenseFile[],
    targetFolderId: string = 'ROOT',
    mediaClass: UploadFilePath = UploadFilePath.AssetLibraryPath
  ): Observable<void> {
    return this.store.getActiveLibraryContext$().pipe(
      switchMap(({ brandId }) => {
        return this.store.multiFileUploadState$.pipe(
          take(1),
          switchMap(currentState => {
            if (!files?.length) {
              return throwError(() => new Error('At least one file is required for a multi-file upload batch.'));
            }
            if (currentState.status !== 'idle' && currentState.status !== 'completed') {
              return throwError(() => new Error('A multi-file upload batch is already running.'));
            }
            const batchId = createMultiUploadBatchId();
            const items = createInitialMultiUploadItems(files);
            this.store.connectToMultiUploadEvent({
              type: 'BatchStarted',
              scope: 'batch',
              batchId,
              items,
              context: {
                brandId,
                targetFolderId,
                mediaClass
              }
            });
            return this.store.multiFileUploadState$.pipe(
              filter(state => state.batchId === batchId && state.status === 'completed'),
              take(1),
              tap(state => this.publishMultiUploadResultToast(state.completedCount)),
              map((): void => undefined)
            );
          })
        );
      })
    );
  }

  /**
   * Add media to Media Manager by referencing an already-uploaded asset (from another mediaClass) rather
   * than re-uploading it. Backed by the copyAssetToTarget$ API which duplicates the source object into the
   * asset library bucket.
   */
  copyAssetToMediaManager$(sourceAsset: Asset, targetFolderId: string = 'ROOT'): Observable<void> {
    const normalizedTargetFolderId = normalizeFolderIdForAssetLibrary(targetFolderId);
    if (!sourceAsset?.id?.trim() || !sourceAsset?.md5Hash?.trim()) {
      return throwError(() => new Error('Media must be available before it can be added to Media Manager.'));
    }
    return this.store.getActiveLibraryContext$().pipe(
      switchMap(({ brandId }) => {
        return of(this.companyId).pipe(
          take(1),
          switchMap(companyId => {
            if (!companyId) {
              return throwError(() => new Error('Company ID is not available for adding media to Media Manager.'));
            }
            const req = new CopyAssetToTargetRequest();
            req.assetId = sourceAsset.id;
            req.md5Hash = sourceAsset.md5Hash;
            req.targetMediaClass = UploadFilePath.AssetLibraryPath;
            req.metadata = new Map<string, string>();
            req.metadata.set('CompanyId', companyId.toString());
            req.metadata.set('ParentFolderId', toBackendAssetLibraryParentFolderId(normalizedTargetFolderId));
            return this.assetRepository.copyAssetToTarget$(req, brandId).pipe(
              switchMap(copiedAsset =>
                this.applyCopiedAssetToMediaManager$(copiedAsset, normalizedTargetFolderId, brandId)
              ),
              map((): void => undefined)
            );
          })
        );
      })
    );
  }

  private applyCopiedAssetToMediaManager$(
    copiedAsset: Asset,
    targetFolderId: string,
    brandId: string
  ): Observable<Asset> {
    if (!copiedAsset?.isValid()) {
      return throwError(() => new Error('Copied media was not returned from Media Manager.'));
    }
    return this.store.getBrandLibraryContext$(brandId).pipe(
      switchMap(({ filesystem, assetLibrary }) => {
        const normalizedCopiedAsset = normalizeCopiedAssetForMediaManager(copiedAsset, targetFolderId);
        const inMemoryPatchSucceeded = applyUploadedAssetToInMemoryState(
          normalizedCopiedAsset,
          filesystem,
          assetLibrary
        );
        if (!inMemoryPatchSucceeded) {
          return this.store.refreshBrandAssetLibrary$(brandId).pipe(map(() => normalizedCopiedAsset));
        }
        this.store.notifyFilesystemChanged('asset copied');
        return of(normalizedCopiedAsset);
      })
    );
  }

  /** Reset the batch state to 'idle', dropping all item states. */
  clearMultiFileUploadState(): void {
    this.store.connectToMultiUploadEvent({
      type: 'BatchCleared',
      scope: 'batch'
    });
  }

  /** Drop failed items from the batch state without touching completed items. */
  removeFailedMultiUploadItems(): void {
    this.store.connectToMultiUploadEvent({
      type: 'FailedUploadItemsRemoved',
      scope: 'batch'
    });
  }

  /**
   * Append additional files to an already-completed batch. Rejects if the batch is not yet completed. Only
   * fires the result toast for newly-completed items, not the ones already in the batch.
   */
  appendMultiUploadAssets$(
    files: BudsenseFile[],
    targetFolderId: string = 'ROOT',
    mediaClass: UploadFilePath = UploadFilePath.AssetLibraryPath
  ): Observable<void> {
    return this.store.getActiveLibraryContext$().pipe(
      switchMap(({ brandId }) => {
        return this.store.multiFileUploadState$.pipe(
          take(1),
          switchMap(currentState => {
            if (!files?.length) {
              return throwError(() => new Error('At least one file is required for a multi-file upload batch.'));
            }
            if (currentState.status !== 'completed') {
              return throwError(() => new Error('Can only append to a completed multi-file upload batch.'));
            }
            const existingCompleted = currentState.itemStates.filter(item => item.status === 'completed');
            const newItems = createInitialMultiUploadItems(files);
            const batchId = createMultiUploadBatchId();
            const context = currentState.context ?? { brandId, targetFolderId, mediaClass };
            this.store.connectToMultiUploadEvent({
              type: 'BatchStarted',
              scope: 'batch',
              batchId,
              items: [...existingCompleted, ...newItems],
              context
            });
            return this.store.multiFileUploadState$.pipe(
              filter(state => state.batchId === batchId && state.status === 'completed'),
              take(1),
              tap(state => this.publishMultiUploadResultToast(state.completedCount, existingCompleted.length)),
              map((): void => undefined)
            );
          })
        );
      })
    );
  }

  private publishMultiUploadResultToast(completedCount: number, alreadyCompletedCount: number = 0): void {
    const newlyCompletedCount = completedCount - alreadyCompletedCount;
    if (newlyCompletedCount <= 0) return;
    this.store.publishMediaManagerSuccessMessage(`${newlyCompletedCount} media Uploaded`);
  }

  /**
   * shareReplay(1) will keep the domain model's multiFileUploadState$ pipeline alive by taking one
   * subscription eagerly. Without this the pipeline would only warm up when a consumer subscribed,
   * causing early events to be missed.
   */
  private turnOnMultiUploadStatePipeline(): void {
    this.store.multiFileUploadState$.subscribe().unsubscribe();
  }

  /**
   * Route BatchStarted events into the actual upload work. Each batch runs its items through validation +
   * S3 PUT + processing wait, feeding per-item events back into the reducer to drive the visible state.
   */
  private setupMultiUploadLaunchSub(): void {
    const multiUploadLaunchSub = this.store.multiUploadEvents$
      .pipe(
        filter((event): event is MediaManagerMultiUploadBatchEvent => event.scope === 'batch'),
        filter((event): event is BatchStartedEvent => event.type === 'BatchStarted'),
        concatMap(event => this.launchMultiUploadBatch$(event.items, event.context))
      )
      .subscribe(event => this.store.connectToMultiUploadEvent(event));
    this.pushSub(multiUploadLaunchSub);
  }

  /**
   * When every item in a batch has reached a terminal state, the reducer flips the batch to 'refreshing'.
   * This sub notices, kicks off any view refresh work, and emits ViewsRefreshed (or BatchFailed on error)
   * to flip the batch to 'completed'.
   */
  private setupMultiUploadRefreshConductorSub(): void {
    const multiUploadRefreshConductorSub = this.store.multiFileUploadState$
      .pipe(
        filter(state => state.status === 'refreshing' && exists(state.batchId)),
        concatMap(state => {
          return this.refreshMultiUploadFilesystemViews$(state.context?.brandId).pipe(
            map(() => {
              return {
                type: 'ViewsRefreshed',
                scope: 'batch',
                batchId: state.batchId
              } as MediaManagerMultiUploadBatchEvent;
            }),
            catchError(err => {
              const errorMessage = err?.message || err?.error?.message || 'Multi-file upload refresh failed.';
              return of({
                type: 'BatchFailed',
                scope: 'batch',
                batchId: state.batchId,
                errorMessage
              } as MediaManagerMultiUploadBatchEvent);
            })
          );
        })
      )
      .subscribe(event => this.store.connectToMultiUploadEvent(event));
    this.pushSub(multiUploadRefreshConductorSub);
  }

  private refreshMultiUploadFilesystemViews$(batchBrandId?: string): Observable<void> {
    return this.store.getActiveLibraryContext$().pipe(
      take(1),
      tap(({ brandId: activeBrandId }) => {
        if (batchBrandId === activeBrandId) {
          this.store.notifyFilesystemChanged('multi upload batch completed');
        }
      }),
      map((): void => undefined)
    );
  }

  private launchMultiUploadBatch$(
    items: MediaManagerMultiUploadItemState[],
    context: MediaManagerMultiUploadBatchContext
  ): Observable<MediaManagerMultiUploadItemEvent> {
    const validationResults$ = from(items.filter(i => i.status === 'pending-validation')).pipe(
      map(item => ({ item, event: buildMultiUploadValidationEvent(item) })),
      share()
    );
    const validationEvents$ = validationResults$.pipe(map(x => x.event));
    const uploadEvents$ = validationResults$.pipe(
      filter(x => x.event.type === 'FileValidationPassed'),
      // Upload files in parallel up to the concurrency cap. Per-item events are keyed by file, so
      // interleaving across files is safe for the state reducer.
      mergeMap(x => this.launchValidatedMultiUploadItem$(x.item, context), this.maxConcurrentMultiUploads)
    );
    return concat(validationEvents$, uploadEvents$);
  }

  private launchValidatedMultiUploadItem$(
    item: MediaManagerMultiUploadItemState,
    context: MediaManagerMultiUploadBatchContext
  ): Observable<MediaManagerMultiUploadItemEvent> {
    const req = this.buildMultiUploadRequest(item.file, context.mediaClass);
    const uploadEvents$ = this.resolveUploadRequest$(req, context.targetFolderId, context.brandId).pipe(
      switchMap(normalizedReq => {
        return this.createMultiUploadItemProcessingContext$(
          normalizedReq,
          context.targetFolderId,
          context.brandId
        ).pipe(
          switchMap(processingContext => {
            // Start reconciliation before the PUT, then emit the upload lifecycle events in order.
            this.startMultiUploadProcessingListener(item.file, processingContext);
            // emit FileUploadPrepared
            // emit FileUploadStarted
            // run putPreparedAsset$
            // when PUT succeeds, emit FileUploadCompleted
            return concat(
              of({
                type: 'FileUploadPrepared',
                scope: 'item',
                file: item.file,
                fileName: normalizedReq.fileName,
                processingContext
              } as MediaManagerMultiUploadItemEvent),
              of({
                type: 'FileUploadStarted',
                scope: 'item',
                file: item.file
              } as MediaManagerMultiUploadItemEvent),
              this.putPreparedAsset$(item.file, normalizedReq, context.brandId).pipe(
                map(() => {
                  return {
                    type: 'FileUploadCompleted',
                    scope: 'item',
                    file: item.file
                  } as MediaManagerMultiUploadItemEvent;
                })
              )
            );
          })
        );
      }),
      catchError(err => {
        const errorMessage = err?.message || err?.error?.message || 'Asset upload failed.';
        return of({
          type: 'FileUploadFailed',
          scope: 'item',
          file: item.file,
          errorMessage
        } as MediaManagerMultiUploadItemEvent);
      })
    );
    return uploadEvents$;
  }

  private startMultiUploadProcessingListener(
    file: BudsenseFile,
    processingContext: MediaManagerMultiUploadItemProcessingContext
  ): void {
    const uploadFailed$ = this.store.multiUploadEvents$.pipe(
      filter(event => event.scope === 'item' && event.type === 'FileUploadFailed' && event.file === file),
      take(1)
    );
    const uploadCompleted$ = this.store.multiUploadEvents$.pipe(
      filter(event => event.scope === 'item' && event.type === 'FileUploadCompleted' && event.file === file),
      take(1)
    );
    // Listen before the PUT and replay first result so fast websocket responses are not missed before
    // FileUploadCompleted.
    const processingResult$ = this.waitForUploadedAsset$(
      processingContext.normalizedReq,
      processingContext.existingAssetIds,
      processingContext.targetFolderId,
      processingContext.brandId,
      file,
      false,
      false
    ).pipe(takeUntil(uploadFailed$), shareReplay({ bufferSize: 1, refCount: true }));
    const earlyProcessingListenerSub = processingResult$.subscribe();
    this.pushSub(earlyProcessingListenerSub);
    const processingEventSub = uploadCompleted$
      .pipe(
        switchMap(() => processingResult$),
        map((resolvedUpload: MediaManagerResolvedUploadAsset) => {
          return {
            type: 'FileProcessingCompleted',
            scope: 'item',
            file,
            uploadedAsset: resolvedUpload.asset,
            resolutionMessage: resolvedUpload.resolutionMessage
          } as MediaManagerMultiUploadItemEvent;
        }),
        catchError(err => {
          const errorMessage = err?.message || err?.error?.message || 'Asset processing failed.';
          return of({
            type: err?.name === 'TimeoutError' ? 'FileProcessingTimedOut' : 'FileProcessingFailed',
            scope: 'item',
            file,
            errorMessage
          } as MediaManagerMultiUploadItemEvent);
        }),
        takeUntil(uploadFailed$)
      )
      .subscribe(event => this.store.connectToMultiUploadEvent(event));
    this.pushSub(processingEventSub);
  }

  private createMultiUploadItemProcessingContext$(
    normalizedReq: GenerateUploadUrlRequest,
    targetFolderId: string,
    brandId: string
  ): Observable<MediaManagerMultiUploadItemProcessingContext> {
    return this.store.getBrandLibraryContext$(brandId).pipe(
      map(({ assetLibrary }) => {
        return {
          brandId,
          normalizedReq,
          existingAssetIds: new Set(assetLibrary.assets?.map(asset => asset.getUniqueIdentifier()) ?? []),
          targetFolderId
        };
      })
    );
  }

  private generateUploadUrl$(req: GenerateUploadUrlRequest, brandId?: string): Observable<SignedUploadUrl> {
    const preflightErrors = preflightGenerateUploadUrlValidation(req);
    if (preflightErrors) {
      return throwError(() => new Error(preflightErrors));
    }
    return this.assetRepository.generateUploadUrl$(req, brandId);
  }

  private putPreparedAsset$(
    file: BudsenseFile,
    normalizedReq: GenerateUploadUrlRequest,
    brandId: string
  ): Observable<void> {
    return forkJoin([this.generateUploadUrl$(normalizedReq, brandId), this.userDomainModel.userId$.pipe(take(1))]).pipe(
      switchMap(([signedUploadUrl, userId]) => {
        return this.assetRepository.putImageUploadUrl$(
          signedUploadUrl.url,
          file.url.toString(),
          normalizedReq.fileName,
          userId,
          this.buildUploadPutHeaders(normalizedReq)
        );
      }),
      map((): void => undefined)
    );
  }

  private resolveUploadRequest$(
    req: GenerateUploadUrlRequest,
    targetFolderId: string = 'ROOT',
    brandId?: string
  ): Observable<GenerateUploadUrlRequest> {
    if (req?.mediaClass !== UploadFilePath.AssetLibraryPath) {
      return of(this.normalizeGenerateUploadUrlRequest(req, targetFolderId));
    }
    if (!brandId) {
      return throwError(() => new Error('A brand id is required for an asset-library upload.'));
    }
    return this.store.getBrandLibraryContext$(brandId).pipe(
      map(({ libraryKey }) => this.normalizeGenerateUploadUrlRequest(req, targetFolderId, String(libraryKey))),
      catchError(err => {
        const errorMessage = err?.message || err?.error?.message || 'The brand asset library is unavailable.';
        return throwError(() => new Error(`Cannot upload to the brand asset library: ${errorMessage}`));
      })
    );
  }

  private buildMultiUploadRequest(
    file: BudsenseFile,
    mediaClass: UploadFilePath = UploadFilePath.AssetLibraryPath
  ): GenerateUploadUrlRequest {
    const sanitizedFileName = MediaUtils.getFileNameWithoutTimestamp(file?.name ?? '');
    const req = new GenerateUploadUrlRequest();
    req.fileName = MediaUtils.appendTimestampToFileName(sanitizedFileName, true);
    req.mediaClass = mediaClass;
    req.mediaId = '';
    req.mediaType = file?.getMediaType?.() ?? null;
    req.metadata = new Map<string, string>();
    return req;
  }

  private normalizeGenerateUploadUrlRequest(
    req: GenerateUploadUrlRequest,
    targetFolderId: string = 'ROOT',
    companyId?: string
  ): GenerateUploadUrlRequest {
    const normalizedTargetFolderId = targetFolderId?.trim() || 'ROOT';
    const backendParentFolderId = normalizedTargetFolderId === 'ROOT' ? '' : normalizedTargetFolderId;
    const metadata = new Map<string, string>();
    new Map<string, string>(req?.metadata ?? []).forEach((value, key) => {
      if (!key?.trim()) {
        return;
      }
      if (key === 'Tags') {
        const normalizedTags = value
          ?.split(',')
          ?.map(tag => tag?.trim())
          ?.filter(tag => exists(tag))
          ?.join(',');
        if (normalizedTags) {
          metadata.set(key, normalizedTags);
        }
        return;
      }
      const normalizedValue = value?.trim();
      if (normalizedValue) {
        metadata.set(key, normalizedValue);
      }
    });
    if (req?.mediaClass === UploadFilePath.AssetLibraryPath && companyId?.trim()) {
      metadata.set('CompanyId', companyId.trim());
    }
    metadata.set('ParentFolderId', backendParentFolderId);
    const normalizedReq = new GenerateUploadUrlRequest();
    normalizedReq.fileName = StringUtils.normalizeCharacters(req?.fileName ?? '');
    normalizedReq.mediaClass = req?.mediaClass;
    normalizedReq.mediaId = req?.mediaId ?? '';
    normalizedReq.mediaType = req?.mediaType;
    normalizedReq.metadata = metadata;
    return normalizedReq;
  }

  /**
   * Race the four possible upload-completion signals: WS transcode-completed, WS duplicate-detected, WS
   * asset-uploaded, and asset-library refetch fallback. First arrival wins. Times out per media type.
   */
  private waitForUploadedAsset$(
    req: GenerateUploadUrlRequest,
    existingIds: Set<string>,
    targetFolderId: string,
    brandId: string,
    file: BudsenseFile,
    refreshViews: boolean = true,
    refetchOnError: boolean = true
  ): Observable<MediaManagerResolvedUploadAsset> {
    return race([
      this.buildUploadAssetFailed$(req),
      this.buildUploadAssetDuplicate$(req, existingIds, targetFolderId, brandId, refreshViews, refetchOnError),
      this.buildUploadAssetProcessed$(req, existingIds, targetFolderId, brandId, refreshViews, refetchOnError),
      this.buildUploadAssetCompleted$(req, existingIds, targetFolderId, brandId, refreshViews, refetchOnError)
    ]).pipe(
      timeout({ first: this.getUploadCompletionTimeoutMs(file) }),
      catchError(err => {
        if (!refetchOnError) {
          return throwError(() => err);
        }
        return this.store.refreshBrandAssetLibrary$(brandId).pipe(
          map(refreshedAssetLibrary => this.findUploadedAssetInLibrary(refreshedAssetLibrary, req, existingIds)),
          switchMap(uploadedAsset => {
            return exists(uploadedAsset)
              ? of({ asset: uploadedAsset, resolutionMessage: null })
              : throwError(() => err);
          })
        );
      })
    );
  }

  private buildUploadAssetCompleted$(
    req: GenerateUploadUrlRequest,
    existingAssetIds: Set<string>,
    targetFolderId: string,
    brandId: string,
    refreshViews: boolean,
    allowRefetchOnError: boolean
  ): Observable<MediaManagerResolvedUploadAsset> {
    return this.store.assetTranscodeUpdateMessages$.pipe(
      filter(msg => msg?.isFor(req) && msg?.completed()),
      take(1),
      switchMap(msg => {
        if (!msg?.hasAssetWithUniqueId()) {
          return throwError(() => new Error('Transcode completed without a valid asset payload.'));
        }
        return this.assetSync.getCompletedTranscodedAsset$(msg?.getAsset()).pipe(
          switchMap(asset => {
            return this.applyUploadedAsset$(
              asset,
              req,
              existingAssetIds,
              targetFolderId,
              brandId,
              refreshViews,
              allowRefetchOnError
            );
          }),
          map(resolvedAsset => ({ asset: resolvedAsset, resolutionMessage: null }) as MediaManagerResolvedUploadAsset)
        );
      })
    );
  }

  private buildUploadAssetFailed$(req: GenerateUploadUrlRequest): Observable<never> {
    return merge(
      this.store.assetTranscodeUpdateMessages$.pipe(filter(m => m?.isFor(req) && m?.errored())),
      this.store.assetTranscodeUpdateMessages$.pipe(filter(m => m?.isFor(req) && m?.canceled()))
    ).pipe(
      take(1),
      switchMap(msg => throwError(() => new Error(msg?.getErrorMessage() || 'Video transcoding failed.')))
    );
  }

  private buildUploadAssetDuplicate$(
    req: GenerateUploadUrlRequest,
    existingAssetIds: Set<string>,
    targetFolderId: string,
    brandId: string,
    refreshViews: boolean,
    allowRefetchOnError: boolean
  ): Observable<MediaManagerResolvedUploadAsset> {
    return this.store.duplicateAssetMessages$.pipe(
      filter(msg => msg?.isFor(req)),
      take(1),
      switchMap(msg => {
        if (!msg?.payload) {
          return throwError(() => {
            return new Error(msg?.userMessage || `Asset '${req.fileName}' already exists.`);
          });
        }
        return this.applyUploadedAsset$(
          msg.payload,
          req,
          existingAssetIds,
          targetFolderId,
          brandId,
          refreshViews,
          allowRefetchOnError
        ).pipe(
          switchMap(asset => {
            return this.store.getBrandLibraryContext$(brandId).pipe(
              map(({ filesystem }) => ({
                asset,
                resolutionMessage: msg?.duplicateAssetMessage(asset, filesystem)
              }))
            );
          })
        );
      })
    );
  }

  private buildUploadAssetProcessed$(
    req: GenerateUploadUrlRequest,
    existingAssetIds: Set<string>,
    targetFolderId: string,
    brandId: string,
    refreshViews: boolean,
    allowRefetchOnError: boolean
  ): Observable<MediaManagerResolvedUploadAsset> {
    return this.store.assetUploadedMessages$.pipe(
      filter(msg => msg?.isFor(req)),
      filter(msg => !msg?.payload?.isVideo() || msg?.payload?.outputProcessed),
      take(1),
      switchMap(msg => {
        return this.applyUploadedAsset$(
          msg?.payload,
          req,
          existingAssetIds,
          targetFolderId,
          brandId,
          refreshViews,
          allowRefetchOnError
        ).pipe(
          map(resolvedAsset => ({ asset: resolvedAsset, resolutionMessage: null }) as MediaManagerResolvedUploadAsset)
        );
      })
    );
  }

  private applyUploadedAsset$(
    uploadedAsset: Asset,
    req: GenerateUploadUrlRequest,
    existingAssetIds: Set<string>,
    targetFolderId: string,
    brandId: string,
    refreshViews: boolean = true,
    allowRefetchOnError: boolean = true
  ): Observable<Asset> {
    return this.store.getBrandLibraryContext$(brandId).pipe(
      switchMap(({ filesystem, assetLibrary }) => {
        const normalizedUploadedAsset = normalizeUploadedAssetForTargetFolder(uploadedAsset, targetFolderId);
        return this.applyNormalizedAssetToInMemoryState$(
          normalizedUploadedAsset,
          req,
          existingAssetIds,
          brandId,
          refreshViews,
          allowRefetchOnError,
          filesystem,
          assetLibrary
        );
      })
    );
  }

  private applyNormalizedAssetToInMemoryState$(
    normalizedUploadedAsset: Asset,
    req: GenerateUploadUrlRequest,
    existingAssetIds: Set<string>,
    brandId: string,
    refreshViews: boolean,
    allowRefetchOnError: boolean,
    filesystem: MediaManagerFileSystem,
    assetLibrary: MediaManagerAssetLibraryDto
  ): Observable<Asset> {
    const inMemoryPatchSucceeded = applyUploadedAssetToInMemoryState(normalizedUploadedAsset, filesystem, assetLibrary);
    if (!inMemoryPatchSucceeded) {
      // We prefer incremental in-memory reconciliation here so successful batch uploads do not pay for
      // an additional assetLibrary fetch and filetree rebuild. If upload-related drift or reconciliation
      // failures become a recurring problem in the future, consider falling back to a full
      // brand-scoped asset-library sync after batch processing completes while the root cause is investigated.
      return this.refetchAndFindUploadedAsset$(
        req,
        existingAssetIds,
        brandId,
        'Uploaded asset could not be located in the refreshed MediaManager assetLibrary.',
        allowRefetchOnError
      );
    }
    if (refreshViews) {
      this.store.notifyFilesystemChanged('asset uploaded');
    }
    return of(normalizedUploadedAsset);
  }

  private refetchAndFindUploadedAsset$(
    req: GenerateUploadUrlRequest,
    existingAssetIds: Set<string>,
    brandId: string,
    errorMessage: string,
    allowRefetchOnError: boolean
  ): Observable<Asset> {
    if (!allowRefetchOnError) {
      return throwError(() => new Error(errorMessage));
    }
    return this.store.refreshBrandAssetLibrary$(brandId).pipe(
      map(refreshedAssetLibrary => this.findUploadedAssetInLibrary(refreshedAssetLibrary, req, existingAssetIds)),
      switchMap(asset => (asset ? of(asset) : throwError(() => new Error(errorMessage))))
    );
  }

  private buildUploadPutHeaders(req: GenerateUploadUrlRequest): Map<string, string> {
    const headers = new Map<string, string>();
    if (req.mediaClass !== UploadFilePath.AssetLibraryPath) {
      return headers;
    }
    const parentFolderId = req.metadata?.get('ParentFolderId');
    const tags = req.metadata?.get('Tags')?.trim();
    const assetLibraryType = req.metadata?.get('AssetLibraryType')?.trim();
    if (typeof parentFolderId === 'string') {
      headers.set('x-amz-meta-parentfolderid', parentFolderId);
    }
    if (tags) {
      headers.set('x-amz-meta-tags', tags);
    }
    if (assetLibraryType) {
      headers.set('x-amz-meta-assetlibrarytype', assetLibraryType);
    }
    return headers;
  }

  private getUploadCompletionTimeoutMs(file: BudsenseFile): number {
    if (file?.isVideo?.()) {
      return 25 * 60000;
    }
    return Math.max(file.getUploadDelay() + 60000, 90000);
  }

  private findUploadedAssetInLibrary(
    assetLibrary: MediaManagerAssetLibraryDto,
    req: GenerateUploadUrlRequest,
    existingAssetIds: Set<string>
  ): Asset | null {
    const normalizedFileName = req.fileName?.trim().toLowerCase();
    const newAssets = assetLibrary.assets?.filter(asset => !existingAssetIds.has(asset.getUniqueIdentifier())) ?? [];
    const fileNameMatches = newAssets.filter(asset => asset.fileName?.trim().toLowerCase() === normalizedFileName);
    if (fileNameMatches.length) {
      return this.sortAssetsByTimestampDesc(fileNameMatches)[0];
    }
    if (newAssets.length) {
      return this.sortAssetsByTimestampDesc(newAssets)[0];
    }
    return null;
  }

  private sortAssetsByTimestampDesc(assets: Asset[]): Asset[] {
    return [...assets].sort((a, b) => (b?.timestamp ?? 0) - (a?.timestamp ?? 0));
  }
}
